/**
 * The JSON messages of the voice WebSocket `/ws/audio/{session}` (docs/NEW-UI/protocollo.md §6),
 * and what each one does to the voice activity the screen shows.
 */
import type { AecMode } from '../core/flags';
import type { VoiceActivity } from './controller';

export type VoiceServerEvent =
  | { type: 'listening_start' }
  | { type: 'processing_start' }
  | { type: 'processing_end'; reason?: string }
  | { type: 'assistant_audio_start'; avatar_epoch: number | null }
  | { type: 'assistant_audio_end'; total_bytes?: number }
  | { type: 'filler_audio_start' }
  | { type: 'filler_audio_end' }
  | { type: 'barge_in_ack' }
  | { type: 'recovery_suggestion'; message: string }
  /** Who removes the echo before the audio reaches the backend, as its config declares (sent on connect). */
  | { type: 'audio_chain'; canceller: string }
  | { type: 'assistant_message' };

export type VoiceClientMessage =
  | { type: 'mic_settings'; [key: string]: unknown }
  | { type: 'playback_started' }
  | { type: 'playback_ended' }
  | { type: 'stop_voice' }
  /** The microphone muted: a sentence half heard is dropped (backend voice_mute.py). */
  | { type: 'mic_off' }
  | { type: 'action'; button_id: string; data: Record<string, unknown> }
  | { type: 'text'; text: string };

/** A text frame → a known event, or null (logged by the caller). */
export function parseVoiceEvent(text: string): VoiceServerEvent | null {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object' || typeof (data as { type?: unknown }).type !== 'string') return null;
  const o = data as Record<string, unknown>;
  switch (o.type) {
    case 'listening_start':
    case 'processing_start':
    case 'filler_audio_start':
    case 'filler_audio_end':
    case 'barge_in_ack':
    case 'assistant_message':
      return { type: o.type };
    case 'processing_end':
      return typeof o.reason === 'string' ? { type: 'processing_end', reason: o.reason } : { type: 'processing_end' };
    case 'assistant_audio_start':
      return { type: 'assistant_audio_start', avatar_epoch: typeof o.avatar_epoch === 'number' ? o.avatar_epoch : null };
    case 'assistant_audio_end':
      return typeof o.total_bytes === 'number' ? { type: 'assistant_audio_end', total_bytes: o.total_bytes } : { type: 'assistant_audio_end' };
    case 'recovery_suggestion':
      return { type: 'recovery_suggestion', message: String(o.message ?? '') };
    case 'audio_chain':
      return typeof o.canceller === 'string' ? { type: 'audio_chain', canceller: o.canceller } : null;
    default:
      return null;
  }
}

/**
 * The browser capture for a canceller the backend declares: someone before the browser removes the echo
 * (`hardware`, `os`) → the browser must not; `browser` → it must. `none` (no hypothesis) or unknown → null: unchanged.
 */
export function aecForCanceller(canceller: string): AecMode | null {
  switch (canceller) {
    case 'hardware':
    case 'os': return 'os';
    case 'browser': return 'browser';
    default: return null; // `none` is no hypothesis: the browser's AEC stays, or a laptop hears its own voice
  }
}

/** The activity after an event; `null` = unchanged. Speaking ends when the reply drains, not here. */
export function activityAfter(event: VoiceServerEvent): VoiceActivity | null {
  switch (event.type) {
    case 'listening_start': return 'listening';
    case 'processing_start': return 'processing';
    case 'processing_end': return 'idle';
    case 'assistant_audio_start':
    case 'filler_audio_start': return 'speaking';
    case 'barge_in_ack': return 'interrupted';
    default: return null;
  }
}

export function voiceSocketUrl(location: Pick<Location, 'protocol' | 'host'>, sessionId: string): string {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${location.host}/ws/audio/${encodeURIComponent(sessionId)}`;
}
